import {Button, Drawer, Form, Radio, Select} from 'antd';
import {useEffect, useMemo, useRef, useState} from 'react';

import {FormActiveMark} from '../../../../component/form-active-mark/form-active-mark';
import {SelectWithCheckboxes} from '../../../../component/select-with-checkboxes/select-with-checkboxes';
import {Locale} from '../../../../provider/locale/locale';
import {useLocale} from '../../../../provider/locale/locale-hook';
import {useReviewsCatalogList} from '../../../../service/reviews/reviews-catalogs';
import {defaultReviewFilters} from '../../../../service/reviews-and-answers-statistics/filter/reviews-and-answers-statistics-const';
import {
    ReviewsAndAnswersFilterQueriesEnum,
    ReviewsAndAnswersReviewFiltersType,
} from '../../../../service/reviews-and-answers-statistics/filter/reviews-and-answers-statistics-filter-type';
import {useDrawerWidthHack} from '../../../../util/antd/drawer-hook';

import {
    CONTENT_OF_REVIEWS_OPTIONS,
    INCLUDE_DELETED_OPTIONS,
    POSSIBILITY_OF_AN_ANSWER_OPTIONS,
    RATING_SELECT_CONTENT,
    REPLIES_TO_REVIEWS_OPTIONS,
    REVIEW_TYPE_OPTIONS,
    SENTIMENT_OPTIONS,
} from './review-filter-const';
import {ReviewFilterToggleRadio} from './review-filter-toggle-radio';
import * as styles from './review-filter-drawer.scss';

type PropsType = {
    isOpen: boolean;
    onClose: () => void;
    appliedFilters: ReviewsAndAnswersReviewFiltersType;
    onApply: (filters: ReviewsAndAnswersReviewFiltersType) => void;
};

// eslint-disable-next-line max-statements
export function ReviewFilterDrawer(props: PropsType): JSX.Element {
    const {isOpen, onClose, appliedFilters, onApply} = props;

    const {getLocalizedString} = useLocale();
    const drawerWidth: string = useDrawerWidthHack();
    const {data: catalogList, isLoading: isCatalogListLoading} = useReviewsCatalogList();

    const [draft, setDraft] = useState<ReviewsAndAnswersReviewFiltersType>(appliedFilters);
    // Force-remount the seeded sources select when the draft is reset while open.
    const [revision, setRevision] = useState(0);
    const wasOpenRef = useRef(false);

    useEffect(() => {
        if (isOpen && !wasOpenRef.current) {
            setDraft(appliedFilters);
            setRevision((previous) => previous + 1);
        }

        wasOpenRef.current = isOpen;
        // Re-seed only on the open transition so editing is never clobbered.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const sourceOptions = useMemo(
        () => (catalogList || []).map((catalog) => ({value: catalog.id, label: catalog.name || ''})),
        [catalogList]
    );

    function setField(partial: Partial<ReviewsAndAnswersReviewFiltersType>): void {
        setDraft((previous) => ({...previous, ...partial}));
    }

    function handleReset(): void {
        setDraft(defaultReviewFilters);
        setRevision((previous) => previous + 1);
    }

    function handleApply(): void {
        onApply(draft);
        onClose();
    }

    const catalogIds = draft[ReviewsAndAnswersFilterQueriesEnum.CatalogIds];
    const rating = draft[ReviewsAndAnswersFilterQueriesEnum.Rating];
    const sentiments = draft[ReviewsAndAnswersFilterQueriesEnum.Sentiments];
    const withoutText = draft[ReviewsAndAnswersFilterQueriesEnum.WithoutText];
    const isReplied = draft[ReviewsAndAnswersFilterQueriesEnum.IsReplied];
    const actionReply = draft[ReviewsAndAnswersFilterQueriesEnum.ActionReply];
    const target = draft[ReviewsAndAnswersFilterQueriesEnum.Target];
    const includeDeleted = draft[ReviewsAndAnswersFilterQueriesEnum.IncludeDeleted];

    return (
        <Drawer
            footer={
                <div className={styles.ReviewFilterDrawer_footer}>
                    <Button onClick={handleReset} type="link">
                        <Locale stringKey="TEXT__RESET" />
                    </Button>
                    <Button onClick={handleApply} type="primary">
                        <Locale stringKey="TEXT__APPLY" />
                    </Button>
                </div>
            }
            onClose={onClose}
            open={isOpen}
            placement="right"
            title={<Locale stringKey="BUTTON__FILTER" />}
            width={drawerWidth}
        >
            <Form className={styles.ReviewFilterDrawer} layout="vertical">
                <div className={styles.ReviewFilterDrawer_field}>
                    <FormActiveMark isVisible={catalogIds.length > 0} />
                    <Form.Item label={<Locale stringKey="REVIEWS__FILTER__LABEL__SOURCE" />}>
                        <SelectWithCheckboxes<number>
                            disabled={isCatalogListLoading}
                            initialValue={catalogIds}
                            key={`sources-${revision}`}
                            loading={isCatalogListLoading}
                            onConfirm={(value) => setField({[ReviewsAndAnswersFilterQueriesEnum.CatalogIds]: value})}
                            options={sourceOptions}
                            placeholder={getLocalizedString('REVIEWS__FILTER__SELECT_A_SOURCE')}
                            selectAllTitle={<Locale stringKey="REVIEWS__FILTER__ALL__SOURCES" />}
                            virtual={false}
                        />
                    </Form.Item>
                </div>

                <div className={styles.ReviewFilterDrawer_field}>
                    <FormActiveMark isVisible={rating.length > 0} />
                    <Form.Item label={<Locale stringKey="REVIEWS__FILTER__LABEL__RATING" />}>
                        <Select<Array<number>>
                            mode="multiple"
                            onChange={(value) => setField({[ReviewsAndAnswersFilterQueriesEnum.Rating]: value})}
                            placeholder={getLocalizedString('NOTIFICATIONS__REVIEW_FILTER__PLACEHOLDER__RATING')}
                            showSearch={false}
                            value={rating}
                        >
                            {RATING_SELECT_CONTENT}
                        </Select>
                    </Form.Item>
                </div>

                <div className={styles.ReviewFilterDrawer_field}>
                    <FormActiveMark isVisible={sentiments.length > 0} />
                    <Form.Item label={<Locale stringKey="REVIEWS__FILTER__LABEL__TONALITY" />}>
                        <Select<Array<string>>
                            mode="multiple"
                            onChange={(value) => setField({[ReviewsAndAnswersFilterQueriesEnum.Sentiments]: value})}
                            options={SENTIMENT_OPTIONS}
                            placeholder={getLocalizedString('REVIEWS__FILTER__SELECT_TONALITY')}
                            showSearch={false}
                            value={sentiments}
                        />
                    </Form.Item>
                </div>

                <div className={styles.ReviewFilterDrawer_field}>
                    <FormActiveMark isVisible={Boolean(withoutText)} />
                    <Form.Item label={<Locale stringKey="REVIEWS__FILTER__LABEL__CONTENT_OF_REVIEWS" />}>
                        <ReviewFilterToggleRadio
                            onChange={(value) => setField({[ReviewsAndAnswersFilterQueriesEnum.WithoutText]: value})}
                            options={CONTENT_OF_REVIEWS_OPTIONS}
                            value={withoutText}
                        />
                    </Form.Item>
                </div>

                <div className={styles.ReviewFilterDrawer_field}>
                    <FormActiveMark isVisible={Boolean(isReplied)} />
                    <Form.Item label={<Locale stringKey="REVIEWS__FILTER__LABEL__REPLIES_TO_REVIEWS" />}>
                        <ReviewFilterToggleRadio
                            onChange={(value) => setField({[ReviewsAndAnswersFilterQueriesEnum.IsReplied]: value})}
                            options={REPLIES_TO_REVIEWS_OPTIONS}
                            value={isReplied}
                        />
                    </Form.Item>
                </div>

                <div className={styles.ReviewFilterDrawer_field}>
                    <FormActiveMark isVisible={Boolean(actionReply)} />
                    <Form.Item label={<Locale stringKey="REVIEWS__FILTER__LABEL__POSSIBILITY_OF_AN_ANSWER" />}>
                        <ReviewFilterToggleRadio
                            onChange={(value) => setField({[ReviewsAndAnswersFilterQueriesEnum.ActionReply]: value})}
                            options={POSSIBILITY_OF_AN_ANSWER_OPTIONS}
                            value={actionReply}
                        />
                    </Form.Item>
                </div>

                <div className={styles.ReviewFilterDrawer_field}>
                    <FormActiveMark isVisible={Boolean(target)} />
                    <Form.Item label={<Locale stringKey="REVIEWS__FILTER__LABEL__REVIEW_TYPE" />}>
                        <ReviewFilterToggleRadio
                            onChange={(value) => setField({[ReviewsAndAnswersFilterQueriesEnum.Target]: value})}
                            options={REVIEW_TYPE_OPTIONS}
                            value={target}
                        />
                    </Form.Item>
                </div>

                <div className={styles.ReviewFilterDrawer_field}>
                    <FormActiveMark
                        isVisible={
                            includeDeleted !== defaultReviewFilters[ReviewsAndAnswersFilterQueriesEnum.IncludeDeleted]
                        }
                    />
                    <Form.Item label={<Locale stringKey="REVIEW_AND_ANSWER_STATISTICS__FILTER__INCLUDE_DELETED" />}>
                        <Radio.Group
                            className={styles.ReviewFilterDrawer_radioGroup}
                            onChange={(event) =>
                                setField({[ReviewsAndAnswersFilterQueriesEnum.IncludeDeleted]: event.target.value})
                            }
                            optionType="button"
                            options={INCLUDE_DELETED_OPTIONS}
                            size="small"
                            value={includeDeleted}
                        />
                    </Form.Item>
                </div>
            </Form>
        </Drawer>
    );
}
